import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Mail, 
  User, 
  Building2, 
  Shield, 
  UserPlus, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Users,
  Settings,
  Crown,
  MoreHorizontal,
  UserX
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';

interface NewUser {
  email: string;
  fullName: string;
  role: 'admin' | 'member';
  department?: string;
}

interface TeamMember {
  id: string;
  user_id: string;
  role: 'admin' | 'member';
  created_at: string;
  profile: {
    full_name: string;
    email: string;
    avatar_url?: string;
  };
}

export function TeamManagement() {
  const { session } = useAuth();
  const { organization } = useSubscription();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [newUser, setNewUser] = useState<NewUser>({
    email: '',
    fullName: '',
    role: 'member',
    department: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load team members when manage dialog opens
  useEffect(() => {
    if (isManageOpen && organization) {
      loadTeamMembers();
    }
  }, [isManageOpen, organization]);

  const loadTeamMembers = async () => {
    if (!organization) return;
    
    setLoadingTeam(true);
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          id,
          user_id,
          role,
          created_at,
          profile:user_id (
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('organization_id', organization.organizationId);

      if (error) throw error;
      setTeamMembers((data as unknown as TeamMember[]) || []);
    } catch (err) {
      console.error('Error loading team members:', err);
    } finally {
      setLoadingTeam(false);
    }
  };

  const resetForm = () => {
    setNewUser({
      email: '',
      fullName: '',
      role: 'member',
      department: ''
    });
    setError(null);
    setSuccessMessage(null);
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    if (!newUser.email || !newUser.fullName) {
      setError('Email and full name are required');
      setIsLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      // Generate a temporary password
      const tempPassword = Math.random().toString(36).slice(-8) + 'A1!';

      // Call the admin function to create user
      const { error: createError } = await supabase.functions.invoke('admin-create-user', {
        body: {
          email: newUser.email,
          fullName: newUser.fullName,
          role: newUser.role,
          department: newUser.department,
          tempPassword,
          createdBy: session?.user?.id
        }
      });

      if (createError) {
        throw createError;
      }

      setSuccessMessage(
        `Account created for ${newUser.fullName}! An invitation email has been sent to ${newUser.email}.`
      );

      resetForm();
      
      // Auto-close dialog after 2 seconds
      setTimeout(() => {
        setIsAddOpen(false);
        setSuccessMessage(null);
      }, 2000);

    } catch (err) {
      console.error('Account creation error:', err);
      
      if (err instanceof Error) {
        const errorMessage = err.message.toLowerCase();
        if (errorMessage.includes('already exists') || errorMessage.includes('already registered')) {
          setError('This email is already registered in the system.');
        } else if (errorMessage.includes('invalid email')) {
          setError('Please enter a valid email address.');
        } else if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
          setError('You do not have permission to create accounts. Please contact your administrator.');
        } else {
          setError(`Failed to create account: ${err.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return;
    }

    try {
      const { error } = await supabase.functions.invoke('api-remove-member', {
        body: { memberId }
      });

      if (error) throw error;

      // Reload team members
      await loadTeamMembers();
    } catch (err) {
      console.error('Error removing team member:', err);
    }
  };

  const handleAddClose = () => {
    setIsAddOpen(false);
    resetForm();
  };

  // Fallback in case of errors
  try {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl px-3 py-2 transition-all duration-200"
            >
              <Settings size={16} />
              <span className="hidden sm:inline ml-2">Manage</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl border border-slate-200 bg-white shadow-xl">
            <DropdownMenuLabel className="text-slate-700 font-medium">Team Management</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => setIsAddOpen(true)}
              className="rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Team Member
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              onClick={() => setIsManageOpen(true)}
              className="rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Team
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Add Member Dialog */}
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <DialogHeader className="pb-4">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <div className="h-8 w-8 bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex items-center justify-center">
                  <UserPlus className="h-4 w-4 text-white" />
                </div>
                Add Team Member
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Create a new account for your team member. They'll receive an email invitation with login instructions.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateAccount} className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="John Doe"
                    value={newUser.fullName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                    disabled={isLoading}
                    className="pl-9 rounded-lg border-slate-300 focus:border-slate-500 focus:ring-slate-500"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Work Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    required
                    disabled={isLoading}
                    className="pl-9 rounded-lg border-slate-300 focus:border-slate-500 focus:ring-slate-500"
                  />
                </div>
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Role</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value as 'admin' | 'member' }))}
                  disabled={isLoading}
                >
                  <SelectTrigger className="rounded-lg border-slate-300 focus:border-slate-500 focus:ring-slate-500">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border border-slate-200 bg-white shadow-xl">
                    <SelectItem value="member" className="rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-600" />
                        <div>
                          <div className="font-medium text-slate-900">Member</div>
                          <div className="text-xs text-slate-500">Can analyze candidates and view results</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="admin" className="rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-slate-600" />
                        <div>
                          <div className="font-medium text-slate-900">Admin</div>
                          <div className="text-xs text-slate-500">Full access and team management</div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Department (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="department" className="text-sm font-medium text-slate-700">Department <span className="text-slate-400">(Optional)</span></Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="department"
                    type="text"
                    placeholder="Engineering, HR, Marketing..."
                    value={newUser.department}
                    onChange={(e) => setNewUser(prev => ({ ...prev, department: e.target.value }))}
                    disabled={isLoading}
                    className="pl-9 rounded-lg border-slate-300 focus:border-slate-500 focus:ring-slate-500"
                  />
                </div>
              </div>

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="rounded-lg border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              {/* Success Alert */}
              {successMessage && (
                <Alert className="rounded-lg border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleAddClose}
                  disabled={isLoading}
                  className="flex-1 rounded-lg border-slate-300 hover:bg-slate-50"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Create Account
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Team Dialog */}
        <Dialog open={isManageOpen} onOpenChange={setIsManageOpen}>
          <DialogContent className="sm:max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <DialogHeader className="pb-4">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <div className="h-8 w-8 bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex items-center justify-center">
                  <Users className="h-4 w-4 text-white" />
                </div>
                Team Members
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Manage your organization's team members and their access levels.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {loadingTeam ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-600">Loading team members...</span>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">No team members yet</p>
                  <p className="text-slate-500 text-sm">Add your first team member to get started</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {teamMembers.map((member) => (
                    <div 
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.profile.avatar_url} />
                          <AvatarFallback className="bg-slate-200 text-slate-700 font-semibold text-sm">
                            {member.profile.full_name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-slate-900">{member.profile.full_name}</div>
                          <div className="text-sm text-slate-500">{member.profile.email}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={member.role === 'admin' ? 'default' : 'secondary'}
                          className={`${
                            member.role === 'admin' 
                              ? 'bg-slate-900 text-white' 
                              : 'bg-slate-200 text-slate-700'
                          } font-medium px-3 py-1 rounded-lg`}
                        >
                          {member.role === 'admin' && <Crown className="h-3 w-3 mr-1" />}
                          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        </Badge>

                        {member.user_id !== session?.user?.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-lg">
                              <DropdownMenuItem 
                                onClick={() => handleRemoveMember(member.id, member.profile.full_name)}
                                className="text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <UserX className="h-4 w-4 mr-2" />
                                Remove from team
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                <div className="text-sm text-slate-600">
                  {teamMembers.length} team member{teamMembers.length !== 1 ? 's' : ''}
                </div>
                <Button 
                  onClick={() => {
                    setIsManageOpen(false);
                    setIsAddOpen(true);
                  }}
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  } catch (error) {
    console.error('TeamManagement render error:', error);
    return (
      <Button 
        variant="ghost" 
        size="sm"
        className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl px-3 py-2"
      >
        <Settings size={16} />
        <span className="hidden sm:inline ml-2">Manage</span>
      </Button>
    );
  }
} 